import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plug, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal, Select, Spinner, Textarea } from "@/components/ui";
import { useGroups } from "@/pages/admin/Users";
import { api } from "@/lib/api";
import type { Group } from "@/lib/types";

interface McpServer {
  id: string;
  scope: "global" | "user";
  name: string;
  description: string;
  transport: "http" | "sse" | "stdio";
  url: string | null;
  command: string | null;
  args: string[];
  header_names: string[];
  env_names: string[];
  tool_filter: { allow?: string[]; deny?: string[] };
  approval: string[];
  enabled: boolean;
  default_on: boolean;
  groups: Group[];
  menu_group?: string | null;
}

interface ToolInfo {
  name: string;
  description: string;
}

/** Manage MCP servers: global ones (admin) or the user's own. */
export default function McpServers({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const base = admin ? "/api/admin/mcp" : "/api/mcp";
  const servers = useQuery({ queryKey: ["mcp", admin ? "global" : "mine"], queryFn: () => api.get<McpServer[]>(base) });
  const [editing, setEditing] = useState<McpServer | "new" | null>(null);
  const [tested, setTested] = useState<Record<string, ToolInfo[] | string>>({});
  const invalidate = () => qc.invalidateQueries({ queryKey: ["mcp"] });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`${base}/${id}`), onSuccess: invalidate });
  const test = useMutation({
    mutationFn: (id: string) => api.post<ToolInfo[]>(`/api/mcp/${id}/test`),
    onSuccess: (tools, id) => setTested((t) => ({ ...t, [id]: tools })),
    onError: (e: Error, id) => setTested((t) => ({ ...t, [id]: e.message })),
  });

  return (
    <Card
      title={admin ? "Global MCP servers" : "My MCP servers"}
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> Add server
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">
        {admin
          ? "Available to all users, or only to the chosen groups. Global servers may also be local programs (stdio)."
          : "Your own MCP servers (HTTP or SSE). Only you can use them."}
      </p>
      {servers.data?.length === 0 && <p className="text-sm text-zinc-500">No servers yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {servers.data?.map((s) => {
          const result = tested[s.id];
          return (
            <li key={s.id} className="py-2.5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    {s.name} <Badge>{s.transport}</Badge>
                    {!s.enabled && <Badge tone="red">disabled</Badge>}
                    {!s.default_on && <Badge tone="amber">off by default</Badge>}
                    {s.menu_group && <Badge tone="indigo">menu: {s.menu_group}</Badge>}
                    {s.approval.length > 0 && <Badge tone="amber">approval</Badge>}
                    {s.groups.map((g) => (
                      <Badge key={g.id}>{g.name}</Badge>
                    ))}
                  </p>
                  <p className="truncate font-mono text-xs text-zinc-500">{s.transport === "stdio" ? [s.command, ...s.args].join(" ") : s.url}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="icon" onClick={() => test.mutate(s.id)} disabled={test.isPending} aria-label="Test connection" title="Test connection">
                    {test.isPending && test.variables === s.id ? <Spinner size={14} /> : <Plug size={16} />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setEditing(s)} aria-label="Edit">
                    <Pencil size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => confirm(`Delete MCP server ${s.name}?`) && remove.mutate(s.id)} aria-label="Delete">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              {typeof result === "string" && <p className="mt-1 text-xs text-red-600">{result}</p>}
              {Array.isArray(result) && (
                <p className="mt-1 text-xs text-zinc-500">
                  {result.length} tools: {result.map((t) => t.name).join(", ")}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {editing && <ServerForm admin={admin} base={base} server={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={invalidate} />}
    </Card>
  );
}

function linesToMap(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const idx = line.search(/[:=]/);
    if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return out;
}

function list(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function ServerForm({ admin, base, server, onClose, onSaved }: { admin: boolean; base: string; server: McpServer | null; onClose: () => void; onSaved: () => void }) {
  const groups = useGroups();
  const [form, setForm] = useState({
    name: server?.name ?? "",
    description: server?.description ?? "",
    transport: server?.transport ?? "http",
    url: server?.url ?? "",
    command: server?.command ?? "",
    args: (server?.args ?? []).join("\n"),
    headers: "",
    env: "",
    filterMode: server?.tool_filter.allow ? "allow" : "deny",
    filter: (server?.tool_filter.allow ?? server?.tool_filter.deny ?? []).join(", "),
    approval: (server?.approval ?? []).join(", "),
    enabled: server?.enabled ?? true,
    default_on: server?.default_on ?? true,
    group_ids: server?.groups.map((g) => g.id) ?? [],
    menu_group: server?.menu_group ?? "",
  });
  const [replaceHeaders, setReplaceHeaders] = useState(!server);
  const [replaceEnv, setReplaceEnv] = useState(!server);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => {
      const filter = list(form.filter);
      const body = {
        name: form.name,
        description: form.description,
        transport: form.transport,
        url: form.transport === "stdio" ? null : form.url,
        command: form.transport === "stdio" ? form.command : null,
        args: form.transport === "stdio" ? form.args.split("\n").map((a) => a.trim()).filter(Boolean) : [],
        headers: replaceHeaders ? linesToMap(form.headers) : null,
        env: replaceEnv ? linesToMap(form.env) : null,
        tool_filter: filter.length ? { [form.filterMode]: filter } : {},
        approval: list(form.approval),
        enabled: form.enabled,
        default_on: form.default_on,
        group_ids: form.group_ids,
        menu_group: form.menu_group.trim() || null,
      };
      return server ? api.patch(`${base}/${server.id}`, body) : api.post(base, body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <Modal open onClose={onClose} title={server ? `Edit ${server.name}` : "Add MCP server"} wide>
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Name" hint="Letters, digits, - and _. Tools are named <name>__<tool>.">
            <Input value={form.name} onChange={set("name")} pattern="[A-Za-z0-9_\-]{1,40}" required />
          </Label>
          <Label label="Transport">
            <Select value={form.transport} onChange={set("transport")}>
              <option value="http">Streamable HTTP</option>
              <option value="sse">SSE</option>
              {admin && <option value="stdio">stdio (local program)</option>}
            </Select>
          </Label>
        </div>
        <Label label="Description">
          <Input value={form.description} onChange={set("description")} />
        </Label>
        {form.transport === "stdio" ? (
          <>
            <Label label="Command">
              <Input className="font-mono" value={form.command} onChange={set("command")} placeholder="npx" required />
            </Label>
            <Label label="Arguments" hint="One per line.">
              <Textarea className="font-mono text-xs" rows={3} value={form.args} onChange={set("args")} />
            </Label>
            <SecretField label="Environment variables" names={server?.env_names ?? []} replace={replaceEnv} setReplace={setReplaceEnv} value={form.env} onChange={set("env")} />
          </>
        ) : (
          <>
            <Label label="URL">
              <Input className="font-mono" value={form.url} onChange={set("url")} placeholder="http://mcp-flux3.mcp-tools:8000/mcp" required />
            </Label>
            <SecretField
              label="Headers"
              hint={admin ? "Values may use {user.id}, {user.username}, {user.email}, {user.name}." : undefined}
              names={server?.header_names ?? []}
              replace={replaceHeaders}
              setReplace={setReplaceHeaders}
              value={form.headers}
              onChange={set("headers")}
            />
          </>
        )}
        <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
          <Label label="Tool filter">
            <Select value={form.filterMode} onChange={set("filterMode")}>
              <option value="deny">Hide these tools</option>
              <option value="allow">Only these tools</option>
            </Select>
          </Label>
          <Label label="Tool names" hint="Comma separated. Empty = all tools.">
            <Input value={form.filter} onChange={set("filter")} />
          </Label>
        </div>
        <Label label="Ask before running" hint='Comma separated tool names, or * for all tools of this server.'>
          <Input value={form.approval} onChange={set("approval")} />
        </Label>
        {admin && !!groups.data?.length && (
          <div className="space-y-1.5">
            <span className="text-sm font-medium">Only for groups</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {groups.data.map((g) => (
                <Checkbox
                  key={g.id}
                  label={g.name}
                  checked={form.group_ids.includes(g.id)}
                  onChange={() => setForm({ ...form, group_ids: form.group_ids.includes(g.id) ? form.group_ids.filter((x) => x !== g.id) : [...form.group_ids, g.id] })}
                />
              ))}
            </div>
          </div>
        )}
        <Label label="Tools menu group" hint="Optional. Servers with the same name are one switch in a chat's tools menu, e.g. “Internet” for search and fetch.">
          <Input value={form.menu_group} onChange={set("menu_group")} maxLength={60} placeholder="Its own entry" />
        </Label>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox label="Enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <Checkbox label="On by default in new chats" checked={form.default_on} onChange={(e) => setForm({ ...form, default_on: e.target.checked })} />
        </div>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function SecretField({
  label,
  hint,
  names,
  replace,
  setReplace,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  names: string[];
  replace: boolean;
  setReplace: (v: boolean) => void;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) {
  if (!replace)
    return (
      <div className="space-y-1.5">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500">{names.length ? `Stored: ${names.join(", ")}` : "None"}</span>
          <Button type="button" size="sm" variant="secondary" onClick={() => setReplace(true)}>
            Replace
          </Button>
        </div>
      </div>
    );
  return (
    <Label label={label} hint={`One per line as Name: value. Stored encrypted and never shown again.${hint ? ` ${hint}` : ""}`}>
      <Textarea className="font-mono text-xs" rows={3} value={value} onChange={onChange} placeholder="Authorization: Bearer ..." />
    </Label>
  );
}
